import {
  PublicNotFoundResponse,
  type PublicNotFoundResponse as NotFoundData,
} from '@ahora-patagonia/contracts';
import { createApiClient } from '../shared/http/ApiClient.js';
import { fixtureNotFound, fixturesActivos } from '../shared/fixtures/publicFixtures.js';
import { mergeHead } from '../shared/seo/HeadDescriptor.js';
import { defaultHead } from '../shared/seo/defaultHead.js';
import type { LoaderContext, LoaderResult, RouteLoader } from '../shared/ssr/RouteLoader.js';

export const NOT_FOUND_ROUTE_KEY = 'not-found';
export type { NotFoundData };

/**
 * El 404 del sitio es una página con datos (shell + últimas publicadas + búsqueda), no un
 * shell pelado: al lector que llegó con un link roto se le ofrece una salida. Lo usan la
 * rama sin match de `entry-server` y los loaders cuya entidad no existe (nota archivada,
 * sección inexistente).
 *
 * Best-effort a propósito: si la API también falla, la página sale sin últimas — un 404
 * nunca puede convertirse en un 500.
 */
export async function notFoundResult(ctx: LoaderContext): Promise<LoaderResult<NotFoundData>> {
  let data: NotFoundData = {
    shell: { tenant: { nombre: ctx.site.name, slug: '', domain: null, logoUrl: null, primaryColor: null, secondaryColor: null }, nav: [], socialLinks: [], ultimaHoraNombre: 'Última hora', masLeidasNombre: 'Más leídas' },
    ultimas: [],
    ads: {},
  };
  try {
    const fixtures = fixturesActivos();
    if (fixtures) {
      data = fixtureNotFound(fixtures);
    } else {
      const api = createApiClient({
        baseUrl: process.env['SSR_API_URL'] ?? 'http://localhost:3000',
        headers: { host: ctx.headers['host'] ?? '' },
      });
      data = PublicNotFoundResponse.parse(await api.get<unknown>('/public/not-found'));
    }
  } catch {
    /* best-effort: 404 sin últimas */
  }

  return {
    data,
    status: 404,
    head: mergeHead(defaultHead(ctx.site, ctx.url), {
      title: `Página no encontrada — ${ctx.site.name}`,
      robots: 'noindex,follow',
    }),
    cache: { sMaxAge: 0, visibility: 'private' },
  };
}

export const notFoundLoader: RouteLoader<NotFoundData> = (ctx) => notFoundResult(ctx);
